import React, { useEffect, useCallback } from "react"
import { reduxForm, submit, reset, Field } from "redux-form"
import { connect } from "react-redux"
import { Box } from "@infotech/uikit-grid"
import FieldSet from "@infotech/uikit-fieldset"
import FilterModal from "src/components/FilterModal"
import get from "lodash/get"
import allEventTypes from "src/hocs/allEventTypes"
import EventTypeSelect from "src/components/forms/EventTypeSelect"
import DateRangePicker from "src/components/DateRangePicker"
import { CircleIcon } from "@infotech/uikit-extra-icons"
import { selectAllStatus } from "src/engine/modules/status/selectors"
import trim from "lodash/trim"
import Input from "src/components/Input"
import Select from "@infotech/uikit-select"
import { selectStatusAvailable } from "src/engine/modules/features/selectors"
import styles from "./styles.css"

function replaceReservedChars(text) {
  // Escaping only single quotes, cause we use them around our string
  return text.replace(/[\\'();,=!~<>]/g, `\\$&`)
}

function unescapeReservedChars(text) {
  // Escaping only single quotes, cause we use them around our string
  return text.replace(/\\([\\'();,=!~<>])/g, `$1`)
}

const FORM_NAME = `eventFilter`

const EventTypesField = allEventTypes()(({ eventTypes: { data } }) => (
  <Field
    component={EventTypeSelect}
    format={null}
    name="eventTypeId"
    placeholder="Выберите тип события"
    options={data.map(item => ({
      value: item.id,
      label: item.name,
      color: item.color,
    }))}
  />
))

const EventStatusField = connect(
  state => ({
    statuses: selectAllStatus(state),
    formValue: get(state, `form.eventFilter.values.status`),
  }),
  null,
)(({ statuses: { data, status }, changeValue, formValue }) => {
  const isDisabled = get(status, `pending`) || !get(status, `completed`)
  return (
    <Field
      component={Select}
      name="status"
      props={{
        isMulti: true,
        hideSelectedOptions: true,
        placeholder: `Выберите статус`,
        isLoading: get(status, `pending`),
        isDisabled,
        onChange: ({ target: { value } }) => {
          changeValue(`status`, value)
        },
        value: formValue,
        options: data.map(item => ({
          value: item.code,
          label: item.name,
          icon: CircleIcon,
          color: item.color,
        })),
      }}
    />
  )
})

const EventDateRangeField = () => (
  <Field component={DateRangePicker} name="range" />
)

const EventFilterFormComponent = props => {
  const {
    isOpen,
    dirty,
    onCancel,
    dispatch,
    query,
    modelName,
    change,
    isStatusAvailable,
  } = props
  const handleSubmit = () => {
    dispatch(submit(FORM_NAME))
  }

  const resetForm = useCallback(() => dispatch(reset(FORM_NAME)), [])

  const handleCancel = useCallback(() => {
    resetForm()
    onCancel()
  }, [])

  useEffect(() => {
    if (!query) {
      resetForm()
    }
  }, [query])

  return (
    <FilterModal
      modelName={modelName}
      isOpen={isOpen}
      onOk={handleSubmit}
      onCancel={handleCancel}
      width={440}
      dirty={dirty}
      invalid={false}
      title="Фильтр событий"
    >
      <Box p="2md">
        <form>
          {isStatusAvailable && (
            <Box loose pb="2md">
              <FieldSet caption="Статус">
                <EventStatusField changeValue={change} />
              </FieldSet>
            </Box>
          )}
          <FieldSet caption="Тип события">
            <EventTypesField />
          </FieldSet>
          <FieldSet caption="План">
            <Field
              component={Input}
              name="planName"
              props={{
                placeholder: `Введите название плана`,
              }}
            />
          </FieldSet>
          <FieldSet className={styles.RangePicker} caption="Период событий">
            <EventDateRangeField />
          </FieldSet>
        </form>
      </Box>
    </FilterModal>
  )
}

const queryToValues = (rsqlQuery, allStatuses) => {
  if (!rsqlQuery) {
    return null
  }

  const statusesRegex = /;?statusCode=in=\((?<statuses>\S+)(.\);?)/gi
  const statuses = trim(
    get(statusesRegex.exec(rsqlQuery), `groups.statuses`, ``),
    `;`,
  ).split(`,`)
  const statusValues = get(allStatuses, `data`, [])
    .filter(item => statuses.includes(item.code))
    .map(option => ({
      value: option.code,
      label: option.name,
      color: option.color,
    }))

  const eventTypeIdRegex = /;?eventTypeId=in=\((?<eventTypeId>\d+)\);?/gi
  const eventTypeId = parseInt(
    trim(
      get(eventTypeIdRegex.exec(rsqlQuery), `groups.eventTypeId`, ``),
      `;`,
    ) || -1,
    10,
  )

  const planNameRegex = /;?planName=='\*(?<planName>.*)\*';?/gi
  const resultObj = planNameRegex.exec(rsqlQuery)
  const planName = unescapeReservedChars(get(resultObj, `groups.planName`, ``))

  const startDateTimeRegex = /;?startDateTime=lt=(?<startDateTime>\d+);?/gi
  const startDateTime = parseInt(
    trim(
      get(startDateTimeRegex.exec(rsqlQuery), `groups.startDateTime`, ``),
      `;`,
    ) || -1,
    10,
  )

  const endDateTimeRegex = /;?endDateTime=gt=(?<endDateTime>\d+);?/gi
  const endDateTime = parseInt(
    trim(
      get(endDateTimeRegex.exec(rsqlQuery), `groups.endDateTime`, ``),
      `;`,
    ) || -1,
    10,
  )

  const range = [
    startDateTime >= 0 ? endDateTime : undefined,
    endDateTime >= 0 ? startDateTime : undefined,
  ]

  return {
    eventTypeId: eventTypeId > 0 ? eventTypeId : undefined,
    planName: planName.length > 0 ? planName : undefined,
    status: statusValues.length ? statusValues : undefined,
    range,
  }
}

const valuesToQuery = values => {
  const queryParts = []
  if (values.status) {
    queryParts.push(
      `statusCode=in=(${values.status.map(item => item.value).join(`,`)})`,
    )
  }

  if (values.eventTypeId) {
    // find related part
    queryParts.push(`eventTypeId=in=(${values.eventTypeId})`)
  }

  if (values.planName) {
    queryParts.push(`planName=='*${replaceReservedChars(values.planName)}*'`)
  }

  const startDateTime = get(values, `range[0]`)
  const endDateTime = get(values, `range[1]`)
  if (endDateTime) {
    queryParts.push(`startDateTime=lt=${endDateTime}`)
  }
  if (startDateTime) {
    queryParts.push(`endDateTime=gt=${startDateTime}`)
  }

  return queryParts.join(`;`)
}

const EventFilterForm = reduxForm({
  onSubmit: (values, dispatch, props) => {
    props.onOk(valuesToQuery(values))
  },
  enableReinitialize: true,
  form: FORM_NAME,
})(EventFilterFormComponent)

const ConnectedEventFilterForm = connect(
  (state, props) => ({
    initialValues: queryToValues(props.query, selectAllStatus(state)),
    isStatusAvailable: selectStatusAvailable(state),
  }),
  null,
)(EventFilterForm)

export default ConnectedEventFilterForm
